/**
 * Diagnostics the console drives: the log stream, coredump transfers and the
 * cadence their replies arrive at.
 *
 * From the command reference (4.2.0, 3986962), section "Diagnostics and recovery
 * replies". The numbers matter because the firmware drops rather than queues:
 * log batches go out at most every 250 ms, coredump chunks are spaced 100 ms
 * apart, and both bypass the ordinary 512-byte publish queue.
 */

/** `getlogs` and the raw log stream on `<serial>/logRCU`. */
export const LOG_STREAM = {
  /** `lines` absent or non-positive means this many. */
  DEFAULT_LINES: 100,
  /** Fewest lines worth asking for. */
  MIN_LINES: 1,
  /** A positive `lines` is cast and then capped here. */
  MAX_LINES: 128,
  /** A batch is normally about this big. */
  BATCH_BYTES: 4_096,
  /** Hard assembly maximum for one batch. */
  BATCH_MAX_BYTES: 8_192,
  /** At most one batch per this interval. */
  BATCH_INTERVAL_MS: 250,
  /** Lines retained in RAM, regardless of streaming. */
  RAM_LINES: 128,
  /** Longest single line the firmware keeps. */
  LINE_BYTES: 512,
} as const;

/**
 * What the firmware inserts when its ring overflows mid-drain, e.g.
 * `*** 12 lines dropped ***`. It is a line in the stream, not a reply.
 */
export const LOG_DROP_NOTICE_PATTERN = /^\*\*\* (\d+) lines dropped \*\*\*$/;

/** Lines that notice says were dropped, or null when the line is a real log line. */
export const logDropNotice = (line: string): number | null => {
  const match = LOG_DROP_NOTICE_PATTERN.exec(line.trim());
  if (match === null) return null;
  const count = Number.parseInt(match[1] ?? '', 10);
  return Number.isFinite(count) ? count : 0;
};

/** `getcoredump` chunk upload. */
export const COREDUMP_CHUNK = {
  /** Raw bytes per chunk. */
  RAW_BYTES: 3_072,
  /** Base64 characters per chunk. */
  BASE64_CHARS: 4_096,
  SPACING_MS: 100,
} as const;

/** Sizes are shown in binary units: a coredump is a binary image. */
export const BYTE_UNIT = {
  KILO: 1_024,
  MEGA: 1_048_576,
} as const;

/** Keys the S3 import file may carry. */
export const S3_ARRAY_KEY = {
  SCENES: 'key_mappings',
  LED: 'led_ack_mappings',
  DALI: 'dali_ack_mappings',
} as const;

/** `downloads3mapping` refuses files at or above this size. */
export const S3_IMPORT_MAX_BYTES = 32_767;
